import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import { FG_3, PRIMARY_COLOR } from 'components/upb_viewer_beta/css/betaTokens';

// Branded FSA loader: the cube logo split into three segments (one path rotated 0/120/240). The
// segment group steps a third-turn at a time (2.4s loop) while the whole mark pulses its scale and
// each segment fades in a stagger. Reduced motion keeps only the fade — no rotation or pulse.
/* eslint-disable max-len */
const SEG_PATH =
  'M51.96 -70L0 -100L-86.6 -50L-86.6 -35.74L-12.35 7.13L0 0L0 -14.26L-49.55 -42.87L0 -71.48L27.26 -55.74Z';
/* eslint-enable max-len */

// Stepped third-turns: hold at each 120° stop, then ease to the next.
const spinStep = {
  '0%': { transform: 'rotate(0deg)' },
  '22%, 33.33%': { transform: 'rotate(120deg)' },
  '55%, 66.67%': { transform: 'rotate(240deg)' },
  '88%, 100%': { transform: 'rotate(360deg)' },
};
// Each segment dips to near-transparent once per loop; children stagger via animationDelay.
const segFade = {
  '0%, 45%, 100%': { opacity: 1 },
  '18%': { opacity: 0.2 },
};
// Whole mark breathes between the rotation steps.
const pulseScale = {
  '0%, 22%, 33.33%, 55%, 66.67%, 88%, 100%': { transform: 'scale(1)' },
  '11%, 44%, 77%': { transform: 'scale(0.85)' },
};

/**
 * FsaLoader — an on-brand animated loading indicator: the FSA cube logo spinning.
 * Drop-in replacement for a bare <Spinner>; pass a `label` to show text beneath it and `size`
 * (px) to scale the mark. Pass `fullView` to center the loader in the full viewport height
 * (so it fills the page instead of hugging the top) — no per-page wrapper needed.
 */
function FsaLoader({ size, label, fullView }) {
  return (
    <div
      className={css(styles.wrap, fullView && styles.fullView)}
      role="status"
      aria-label={label || 'Loading'}
    >
      <svg
        width={size}
        height={size}
        viewBox="-110 -105 220 210"
        aria-hidden="true"
        className={css(styles.mark)}
      >
        <g className={css(styles.pulse)}>
          {/* Static aspect correction so the segments read as the squared-off cube faces. */}
          <g transform="scale(1.078 1)">
            <g className={css(styles.spin)}>
              <path className={css(styles.seg)} d={SEG_PATH} />
              <path className={css(styles.seg, styles.seg2)} d={SEG_PATH} transform="rotate(120)" />
              <path className={css(styles.seg, styles.seg3)} d={SEG_PATH} transform="rotate(240)" />
            </g>
          </g>
        </g>
      </svg>
      {label ? <div className={css(styles.label)}>{label}</div> : null}
    </div>
  );
}

const styles = StyleSheet.create({
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
  // Fill the viewport and center both axes, so the loader takes the whole view rather than
  // sitting in the top half when the container has no intrinsic height.
  fullView: { justifyContent: 'center', minHeight: '100vh', width: '100%' },
  mark: { color: PRIMARY_COLOR },
  // Whole-mark scale pulse; disabled under reduced motion.
  pulse: {
    animationName: pulseScale,
    animationDuration: '2.4s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  },
  // Segment group steps a third-turn per stop; disabled under reduced motion.
  spin: {
    animationName: spinStep,
    animationDuration: '2.4s',
    animationTimingFunction: 'cubic-bezier(0.7, 0, 0.3, 1)',
    animationIterationCount: 'infinite',
    transformBox: 'fill-box',
    transformOrigin: 'center',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  },
  // Staggered fade kept even under reduced motion (the only remaining cue).
  seg: {
    fill: 'currentColor',
    animationName: segFade,
    animationDuration: '2.4s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
  },
  seg2: { animationDelay: '-1.6s' },
  seg3: { animationDelay: '-0.8s' },
  label: { fontSize: 13, color: FG_3, fontWeight: 500 },
});

FsaLoader.propTypes = {
  size: PropTypes.number,
  label: PropTypes.string,
  fullView: PropTypes.bool,
};

FsaLoader.defaultProps = {
  size: 64,
  label: null,
  fullView: false,
};

export default FsaLoader;
